import { useCallback, useEffect, useMemo, useState } from "react";
import { usePost } from "./API";
import { categorizeSignedURLs, snakeToEnglish, fileNameFromUrl } from "./Util";
import { DataTable, Modal, Badge, Spinner, useToast, Select, Alert, Button, TextArea, Tooltip, Icon, SelectOptionType, Timeline } from "@aidkitorg/component-library";
import { TableAction } from "@aidkitorg/component-library/dist/types/src/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { FieldConfig } from "@aidkitorg/aidkit/lib/documents/filehub";
import { AuditLog } from "@aidkitorg/aidkit/lib/admin_panel/audit_logs/repository/IAuditLogRepository";

interface FileHubDocument {
  url: string;
  created_at: string;
  source: string;
  target_field: string | null;
  latest: boolean | null;
}

function renderMediaPreview(params: {
  imageUrl: string | null,
  videoUrl: string | null,
  audioUrl: string | null,
  alt?: string,
}) {
  const { imageUrl, videoUrl, audioUrl, alt = '' } = params;
  const hasMedia = imageUrl || videoUrl || audioUrl;

  return (
    <div className="overflow-auto my-4">
      {videoUrl && (
        <video className="w-full rounded" controls crossOrigin="anonymous" src={videoUrl} />
      )}
      {audioUrl && (
        <audio className="w-full" controls preload="auto">
          <source src={audioUrl} type="audio/mpeg" />
        </audio>
      )}
      {imageUrl && (
        <img alt={alt} className="max-w-full rounded" src={imageUrl} />
      )}
      {!hasMedia && (
        <p className="text-gray-500 py-8 text-center">Unable to load file preview.</p>
      )}
    </div>
  );
}

function isAuditLog(entry: AuditLog | FileHubDocument): entry is AuditLog {
  return 'event' in entry;
}

function getEntryTimestamp(entry: AuditLog | FileHubDocument): number {
  const raw = isAuditLog(entry) ? entry.timestamp : entry.created_at;
  return new Date(raw).getTime();
}

type HistoryEntry = {
  timestamp: number;
  note?: string;
  action?: 'copy_to' | 'remove';
  targetField?: string;
  source: string;
}

function FileHistory(props: { entries: (AuditLog | FileHubDocument)[] }) {
  if (props.entries.length === 0) {
    return <p className="text-gray-500 py-8 text-center">No history available.</p>;
  }

  // Bucket the events by timestamp - we prefer audit log events over target field movements, but 
  // we are not always guaranteed to have an audit log event for each movement of the file.
  const buckets = props.entries.reduce<Record<number, HistoryEntry>>((acc, entry) => {
    // bucket by 10 second intervals - this should guarantee that a TF movement that happened with an Audit Log event will be grouped together,
    // but nothing will be grouped erroneously.
    const ts = Math.round(getEntryTimestamp(entry) / 10_000);
    acc[ts] ??= { timestamp: ts, source: '' };
    if (isAuditLog(entry)) {
      acc[ts].note = entry.value?.note;
      acc[ts].action = entry.value?.action;
      acc[ts].targetField = entry.value?.target_field;
      acc[ts].source = entry.userName;
    } else {
      if (!acc[ts].targetField) acc[ts].targetField = entry.target_field ?? undefined;
      if (!acc[ts].source) acc[ts].source = entry.source;
    }
    return acc;
  }, {});

  const sortedKeys = Object.keys(buckets).sort((a, b) => Number(b) - Number(a));

  return (
    <Timeline>
      {sortedKeys.map((key, i) => {
        const entry = buckets[Number(key)];
        const next = i + 1 < sortedKeys.length ? buckets[Number(sortedKeys[i + 1])] : null;
        const time = new Date(entry.timestamp * 10_000).toISOString();

        // For a movement that is not an audit log event, it's possible that this is just a change to the row in applicantinfo
        // that did not actually move the document (another document was moved here as well, for example).
        // In this case, do not show this entry.
        // (Note, we compare to next and not prev bc Timeline renders from newest to oldest, 
        // and we care about the FIRST (oldest) movement of a document to a certain place.)
        if (next && !entry.action && next.targetField === entry.targetField) {
          return null;
        }

        if (entry.action) {
          const label = entry.action === 'copy_to'
            ? <>Moved to <code className="text-black">{entry.targetField}</code></>
            : <>Removed from <code className="text-black">{entry.targetField}</code></>;

          return (
            <Timeline.Item key={`action-${key}`} time={time} variant="primary">
              <div>{label} by {entry.source}</div>
              {entry.note && <div className="mt-1 text-gray-500 text-sm">Note: "{entry.note}"</div>}
            </Timeline.Item>
          );
        }

        const targetFieldEl = entry.targetField
          ? <> to field <code className="text-black">{entry.targetField}</code></>
          : null;

        const uploadedBy = entry.source === 'Comms'
          ? <>Uploaded via Comms{targetFieldEl}</>
          : !entry.source || entry.source.toLowerCase() === 'system'
            ? <>Uploaded{targetFieldEl} by applicant</>
            : <>Uploaded{targetFieldEl} by {entry.source}</>;

        return (
          <Timeline.Item key={`doc-${key}`} time={time} variant="primary">
            <span>{uploadedBy}</span>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

export function FileHub(props: { applicant?: string }) {
  const getDocuments = usePost("/applicant/get_documents");
  const getDocumentTargetFields = usePost("/applicant/get_document_target_fields");
  const getSignedURL = usePost("/v2/document/view");
  const postMoveDocument = usePost("/applicant/move_document");
  const getAuditLogs = usePost("/api/admin_panel/user_audit_log");

  // Document target fields are specified in program config as target fields that contain documents.
  const [documentTargetFields, setDocumentTargetFields] = useState<Record<string, FieldConfig>>({});
  const [documents, setDocuments] = useState<FileHubDocument[]>([]);
  const [documentHistory, setDocumentHistory] = useState<Record<string, (AuditLog | FileHubDocument)[]>>({});
  const [loading, setLoading] = useState(true);

  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeFileUrl, setActiveFileUrl] = useState<string | null>(null);

  const { toast } = useToast();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [copyToModalOpen, setCopyToModalOpen] = useState(false);
  const [copyToExplanation, setCopyToExplanation] = useState("");
  const [selectedTargetField, setSelectedTargetField] = useState<SelectOptionType | undefined>(undefined);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeTargetField, setRemoveTargetField] = useState<string | null>(null);
  const [removeExplanation, setRemoveExplanation] = useState("");

  const [postingMoveDocument, setPostingMoveDocument] = useState(false);
  const [postingRemoveDocument, setPostingRemoveDocument] = useState(false);

  const fetchDocumentsAndHistory = useCallback(async () => {
    if (!props.applicant) return;
    const [docsResult, auditLogsResult] = await Promise.all([
      getDocuments({ applicant: props.applicant }),
      getAuditLogs({ filters: { applicants: [props.applicant], events: ['APPLICANT_DOCUMENT_MOVED'] } }),
    ]);

    let history: Record<string, (AuditLog | FileHubDocument)[]> = docsResult.uniqueFiles ?? {};
    for (const log of auditLogsResult?.data?.auditLogs?.data ?? []) {
      if (log.value?.url) {
        history[log.value.url]?.push(log);
      }
    }

    setDocumentHistory(history);
    setDocuments(docsResult?.documents ?? []);
  }, [props.applicant]);

  useEffect(() => {
    if (!props.applicant) return;
    (async () => {
      try {
        setLoading(true);
        const [, targetFieldsResult] = await Promise.all([
          fetchDocumentsAndHistory(),
          getDocumentTargetFields({}),
        ]);
  
        setDocumentTargetFields(targetFieldsResult?.documentTargetFields ?? {});
      } catch (e) {
        console.error("Failed to fetch documents:", e);
        toast({ description: `Failed to fetch documents: ${e}`, variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.applicant]);

  const loadPreview = useCallback(async (url: string) => {
    setActiveFileUrl(url);
    setPreviewImageUrl(null);
    setPreviewVideoUrl(null);
    setPreviewAudioUrl(null);
    setPreviewLoading(true);
    try {
      const result = await getSignedURL({ paths: [url] });
      if (result?.paths?.[url]) {
        const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[url]);
        setPreviewImageUrl(imageFileUrls[0] ?? null);
        setPreviewVideoUrl(videoFileUrls[0] ?? null);
        setPreviewAudioUrl(audioFileUrls[0] ?? null);
      }
    } catch (error) {
      console.error("Failed to fetch signed URL:", error);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const columns = useMemo<ColumnDef<FileHubDocument, any>[]>(() => [
    {
      id: "file",
      header: "File",
      accessorFn: (row) => fileNameFromUrl(row.url),
      cell: ({ row }) => (
        <button
          onClick={() => { loadPreview(row.original.url); setViewModalOpen(true); }}
          className="text-indigo-600 hover:text-indigo-800 underline break-all text-left cursor-pointer bg-transparent border-none p-0"
        >
          {fileNameFromUrl(row.original.url)}
        </button>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.source,
      cell: ({ row }) => <span>{row.original.source?.toLowerCase() === 'system' ? 'Applicant' : row.original.source}</span>,
    },
    {
      id: "target_field",
      header: "Location",
      accessorFn: (row) => row.target_field ? snakeToEnglish(row.target_field) : "--",
      cell: ({ row }) => row.original.target_field ? <div className="flex items-center gap-2">
        <Tooltip 
          variant="light"
          text={<div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}:</span> : <code className="text-black">{row.original.target_field}:</code>}
              {documentTargetFields[row.original.target_field]?.description ? <div className="text-gray-500 text-sm">{documentTargetFields[row.original.target_field]?.description}</div> : <div className="text-gray-500 text-sm">the field that this document lives on.</div>}
            </div>
            <div>
              {row.original.latest 
                ? <span>This document is currently live on this attachment field.</span> 
                : <span>This document was last seen on this attachment, but does not live on this attachment anymore.</span>}
            </div>
          </div>}
        >
          <Icon name="InformationCircleIcon" className="w-4 h-4" />
        </Tooltip>
        {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}</span> : <code className="text-black">{row.original.target_field}</code>}
        {row.original.latest ? <Badge variant="success">Current</Badge> : <></>}
      </div> : "--",
    },
    {
      id: "created_at",
      header: "Last modified",
      accessorFn: (row) => row.created_at,
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return isNaN(date.getTime()) ? row.original.created_at : date.toLocaleString();
      },
    }
  ], [loadPreview, documentTargetFields]);

  const actions = useMemo<TableAction<FileHubDocument>[]>(() => [
    {
      label: "View File",
      onClick: (row: FileHubDocument) => { loadPreview(row.url); setViewModalOpen(true); },
      icon: 'EyeIcon',
    },
    {
      label: "Copy To",
      onClick: (row: FileHubDocument) => {
        setSelectedTargetField(undefined);
        setCopyToExplanation("");
        setCopyToModalOpen(true);
        loadPreview(row.url);
      },
      icon: 'DocumentDuplicateIcon',
    },
    {
      label: "History",
      onClick: (row: FileHubDocument) => {
        setHistoryModalOpen(true);
        loadPreview(row.url);
      },
      icon: 'ClockIcon',
    },
    {
      label: "Remove From Attachment",
      onClick: (row: FileHubDocument) => {
        setRemoveExplanation("");
        setRemoveTargetField(row.target_field);
        setRemoveModalOpen(true);
        loadPreview(row.url);
      },
      icon: 'XMarkIcon',
      hideWhen: (row: FileHubDocument) => !row.latest,
      disableWhen: (row: FileHubDocument) => {
        if (!row.target_field) return true;
        return documents.filter(d => d.target_field === row.target_field && d.latest).length <= 1;
      },
    },
  ], [loadPreview, documents]);

  const targetFieldOptions = useMemo<SelectOptionType[]>(() =>
    Object.entries(documentTargetFields).map(([key, config]) => ({
      label: config.displayName || snakeToEnglish(key),
      value: key,
    })), [documentTargetFields]);

  const selectedFieldDescription = selectedTargetField
    ? documentTargetFields[selectedTargetField.value]?.description
    : undefined;

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">File Hub</h1>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">File Hub</h1>
      {documents.length === 0 ? (
        <p className="text-gray-500">No documents found for this applicant.</p>
      ) : (
        <DataTable
          columns={columns}
          data={documents}
          actions={actions}
          getRowId={(row) => `${row.target_field}-${row.url}`}
          showPagination={true}
          enableSearch={true}
        />
      )}

      <Modal
        open={viewModalOpen}
        onClose={setViewModalOpen}
        title={activeFileUrl ? fileNameFromUrl(activeFileUrl) : ''}
        size="lg"
      >
        {previewLoading ? (
          <p className="text-gray-500 py-8 text-center">Loading file...</p>
        ) : (
          renderMediaPreview({ imageUrl: previewImageUrl, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileUrl ? fileNameFromUrl(activeFileUrl) : '' })
        )}
      </Modal>

      <Modal
        open={historyModalOpen}
        onClose={setHistoryModalOpen}
        title="File History"
        size="lg"
      >
        {previewLoading ? (
          <p className="text-gray-500 py-8 text-center">Loading preview...</p>
        ) : (
          renderMediaPreview({ imageUrl: previewImageUrl, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileUrl ? fileNameFromUrl(activeFileUrl) : '' })
        )}
        <FileHistory entries={activeFileUrl ? (documentHistory[activeFileUrl] ?? []) : []} />
      </Modal>

      <Modal
        open={copyToModalOpen}
        onClose={setCopyToModalOpen}
        title="Copy Document To"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div className="mt-2">Copying this document to a new location will not remove it from its existing location.</div>
          {previewLoading ? (
            <p className="text-gray-500 py-8 text-center">Loading preview...</p>
          ) : (
            renderMediaPreview({ imageUrl: previewImageUrl, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileUrl ?? '' })
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Copy To:</span>
            <div className="flex-1">
              <Select
                label="Location"
                hideLabel
                description=""
                options={targetFieldOptions}
                value={selectedTargetField}
                onChange={(value) => setSelectedTargetField(value)}
                clearable
              />
            </div>
          </div>
          {selectedFieldDescription && selectedTargetField && (
            <Alert variant="primary" className="p-2">
              <div>Field: {selectedTargetField.value}</div>
              <div>Description: {selectedFieldDescription}</div>
            </Alert>
          )}
          <TextArea
            label="Reason for copy"
            placeholder="Briefly explain why this document is being copied..."
            value={copyToExplanation}
            onChange={(e) => setCopyToExplanation(e.target.value)}
            rows={3}
          />
          <Button
            variant="primary"
            loading={postingMoveDocument}
            onClick={async () => {
              if (!props.applicant || !selectedTargetField || !activeFileUrl) return;
              setPostingMoveDocument(true);
              const result = await postMoveDocument({
                applicant: props.applicant,
                target_field: selectedTargetField.value,
                update: { kind: 'copy_to', url: activeFileUrl, note: copyToExplanation },
              });
              setPostingMoveDocument(false);
              if (result?.error) {
                // error will be toasted, leave modal open.
                return;
              }
              setCopyToModalOpen(false);
              await fetchDocumentsAndHistory();
            }}
            isDisabled={!copyToExplanation.trim() || !selectedTargetField}
          >
            Copy
          </Button>
        </div>
      </Modal>

      <Modal
        open={removeModalOpen}
        onClose={setRemoveModalOpen}
        title="Remove From Attachment"
        size="md"
      >
        <div className="flex flex-col gap-4">
          {previewLoading ? (
            <p className="text-gray-500 py-8 text-center">Loading preview...</p>
          ) : (
            renderMediaPreview({ imageUrl: previewImageUrl, videoUrl: previewVideoUrl, audioUrl: previewAudioUrl, alt: activeFileUrl ?? '' })
          )}
          <p className="text-sm text-gray-700">
            This will remove the document from <strong>{removeTargetField ? snakeToEnglish(removeTargetField) : 'this field'}</strong>.
            The document will still be available in the File Hub and can be added back at any time.
          </p>
          <TextArea
            label="Reason for removal"
            placeholder="Briefly explain why this document is being removed..."
            value={removeExplanation}
            onChange={(e) => setRemoveExplanation(e.target.value)}
            rows={3}
          />
          <Button
            variant="secondary"
            loading={postingRemoveDocument}
            onClick={async () => {
              if (!props.applicant || !removeTargetField || !activeFileUrl) return;
              setPostingRemoveDocument(true);
              const result = await postMoveDocument({
                applicant: props.applicant,
                target_field: removeTargetField,
                update: { kind: 'remove', url: activeFileUrl, note: removeExplanation },
              });
              setPostingRemoveDocument(false);
              if (result?.error) {
                // error will be toasted, leave modal open.
                return;
              }
              setRemoveModalOpen(false);
              await fetchDocumentsAndHistory();
            }}
            isDisabled={!removeExplanation.trim()}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}
